import React, { useState, useRef, useEffect, isValidElement } from "react";
import type { ReactElement } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import Button from "../Button/Button";
import Tooltip from "../Tooltip/Tooltip";
import { z } from "zod";

/**
 * FabMenuComponent
 * 
 * Um componente de menu flutuante (FAB) inspirado no Material UI SpeedDial.
 * 
 * Recursos:
 * - Botão principal flutuante (FAB) com ícone e rótulo.
 * - Lista de ações exibidas em diferentes direções (up, down, left, right).
 * - Suporte a animação de entrada/saída e efeito stagger.
 * - Tooltips laterais customizáveis para cada ação, com posicionamento dinâmico conforme a direção do menu.
 * - Suporte a temas claro e escuro.
 * - Acessibilidade: navegação por teclado, foco gerenciado, roles ARIA.
 * - Pode ser controlado (via prop `open`) ou não-controlado (internamente).
 * - Suporte a portal para renderizar fora da árvore principal.
 * 
 * Props:
 * - icon: Ícone do FAB principal.
 * - label: Rótulo do FAB principal (usado como tooltip).
 * - actions: Array de ações (key, label, icon, onClick, etc).
 * - children: Alternativamente, pode passar elementos React como ações.
 * - open: Controla a abertura do menu (opcional).
 * - onOpenChange: Callback ao abrir/fechar o menu.
 * - onClose: Callback ao fechar o menu.
 * - className: Classe extra para o container.
 * - direction: Direção de abertura das ações ("up", "down", "left", "right").
 * - portal: Se true, renderiza o menu via React Portal.
 * - theme: "light" ou "dark".
 * 
 * Exemplo de uso:
 * 
 * <FabMenu
 *   icon={<AddIcon />}
 *   label="Ações rápidas"
 *   actions={[
 *     { key: 'edit', label: 'Editar', icon: <EditIcon />, onClick: handleEdit },
 *     { key: 'delete', label: 'Excluir', icon: <DeleteIcon />, onClick: handleDelete, disabled: true },
 *   ]}
 *   direction="up"
 *   theme="light"
 * />
 * 
 * Observações:
 * - Certifique-se de que as classes Tailwind customizadas estão no safelist do tailwind.config.js.
 * - Para animações avançadas, considere plugins ou CSS custom.
 */

// Zod schemas para validação das props
const FabMenuActionSchema = z.object({
  key: z.string(),
  label: z.string(),
  icon: z.custom<React.ReactElement>().optional(),
  onClick: z.custom<((e?: React.MouseEvent<HTMLButtonElement>) => void)>(),
  loading: z.boolean().optional(),
  tooltip: z.string().optional(),
  color: z.string().optional(),
  disabled: z.boolean().optional(),
  feedback: z.string().nullable().optional(),
});

const FabMenuPropsSchema = z.object({
  icon: z.union([z.null(), z.custom<React.ReactElement>()]).optional(),
  label: z.string().optional(),
  children: z.custom<React.ReactNode>(),
  open: z.boolean().optional(),
  onOpenChange: z.custom<(open: boolean) => void>().optional(),
  onClose: z.custom<() => void>().optional(),
  className: z.string().optional(),
  direction: z.enum(["up", "down", "left", "right"]).optional(),
  portal: z.boolean().optional(),
  theme: z.enum(["light", "dark"]).optional(),
  actions: z.array(FabMenuActionSchema).optional(),
  closeOnAction: z.boolean().optional(), // <-- NOVO
});

type BaseActionProps = {
  tabIndex?: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
  tooltip?: string;
  icon?: React.ReactElement;
  [key: string]: unknown;
};

export type FabActionElement = ReactElement<BaseActionProps>;
type FabMenuProps = z.infer<typeof FabMenuPropsSchema>;

const THEME = {
  light: {
    fabBg: "bg-[#1976d2]", // Azul Material
    fabColor: "text-white",
    fabShadow: "shadow-[0_6px_16px_0_rgba(33,150,243,0.32)]", // Sombra azul Material
    actionBg: "bg-white",
    actionColor: "text-[#1976d2]",
    actionHoverBg: "hover:bg-blue-100",
    actionHoverColor: "hover:text-[#1565c0]",
    tooltipBg: "bg-gray-900",
    tooltipColor: "text-white",
    backdrop: "bg-black/40", // Mais escuro
    focusOutline: "focus-visible:outline-[#1976d2]",
  },
  dark: {
    fabBg: "bg-[#1976d2]",
    fabColor: "text-white",
    fabShadow: "shadow-[0_6px_16px_0_rgba(33,150,243,0.32)]",
    actionBg: "bg-zinc-900",
    actionColor: "text-blue-300",
    actionHoverBg: "hover:bg-zinc-700",
    actionHoverColor: "hover:text-blue-300",
    tooltipBg: "bg-white",
    tooltipColor: "text-zinc-900",
    backdrop: "bg-black/60",
    focusOutline: "focus-visible:outline-blue-400",
  },
};

const FabMenuComponent = React.forwardRef<HTMLButtonElement, FabMenuProps>((props, ref) => {
  // Validação das props com Zod
  FabMenuPropsSchema.parse(props);
  const {
    icon,
    label,
    children,
    open: openProp,
    onOpenChange,
    onClose,
    className,
    direction = "up",
    portal = false,
    theme = "light",
    actions,
    closeOnAction = true, // <-- default
  } = props;

  // Gere um id único para o menu (deve ser antes dos hooks que usam)
  const menuIdRef = useRef(`fab-menu-list-${Math.random().toString(36).slice(2, 10)}`);

  // Normaliza e valida os children
  const normalizedChildren = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<BaseActionProps> => React.isValidElement(child)
  ) as ReactElement<BaseActionProps>[];

  // Em desenvolvimento, avisa se algum filho não for válido
  if (process.env.NODE_ENV !== "production") {
    React.Children.forEach(children, (child, idx) => {
      if (!isValidElement(child)) {
        // eslint-disable-next-line no-console
        console.warn("FabMenu: Todos os children devem ser elementos React válidos (FabActionElement).");
      }
      // NOVO: Recomenda memoização para listas grandes
      if (
        normalizedChildren.length > 10 &&
        isValidElement(child) &&
        !child.key
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `FabMenu: Para listas grandes, recomenda-se memoizar os children ou fornecer uma key única e estável para cada child. Child no índice ${idx} está sem key.`
        );
      }
    });
  }

  const [internalOpen, setInternalOpen] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const [menuState, setMenuState] = useState<"closed" | "opening" | "open" | "closing">("closed");
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const actionRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Função para alterar o estado, respeitando modo controlado/descontrolado
  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
    }
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    // Chama onClose se estiver fechando
    if (!nextOpen && open) {
      onClose?.();
    }
  }, [onOpenChange, isControlled, onClose, open]);

  // Foca o primeiro item quando o menu abrir
  useEffect(() => {
    if (open && actionRefs.current[0]) {
      // Foca o botão dentro do primeiro li
      const btn = actionRefs.current[0]?.querySelector("button");
      btn?.focus();
    }
  }, [open]);

  // Fecha o menu ao pressionar Esc globalmente
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleOpenChange(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, handleOpenChange]);

  // Fecha o menu ao pressionar TAB fora do menu (opcional)
  useEffect(() => {
    if (!open) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      // Verifica se o foco está fora do menu
      const menu = document.getElementById(menuIdRef.current);
      if (menu && document.activeElement && !menu.contains(document.activeElement)) {
        handleOpenChange(false);
      }
    };
    document.addEventListener("keydown", handleTab, true);
    return () => document.removeEventListener("keydown", handleTab, true);
  }, [open, handleOpenChange]);

  // Controla a visibilidade do backdrop
  useEffect(() => {
    if (open) {
      setBackdropVisible(true);
    } else if (backdropVisible) {
      // Aguarda a animação de fade-out antes de esconder o backdrop
      const timeout = setTimeout(() => setBackdropVisible(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [open, backdropVisible]);

  // Controla a animação de abertura/fechamento do menu
  useEffect(() => {
    if (open) {
      setMenuState("opening");
      const timeout = setTimeout(() => setMenuState("open"), 200);
      return () => clearTimeout(timeout);
    } else if (!open && (menuState === "open" || menuState === "opening")) {
      setMenuState("closing");
      const timeout = setTimeout(() => setMenuState("closed"), 200);
      return () => clearTimeout(timeout);
    }
  }, [open, menuState]);

  // Navegação por teclado sensível à direção
  const isVertical = direction === "up" || direction === "down";
  const keyNext = isVertical ? "ArrowDown" : "ArrowRight";
  const keyPrev = isVertical ? "ArrowUp" : "ArrowLeft";

  const handleActionKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    idx: number,
    disabled?: boolean
  ) => {
    if (disabled) return;
    if (e.key === keyNext) {
      e.preventDefault();
      let next = idx;
      const total = normalizedChildren.length || (actions?.length || 0);
      if (total === 0) return;
      do {
        next = (next + 1) % total;
      } while (
        next !== idx &&
        (
          actions
            ? actions[next]?.disabled
            : (
                React.isValidElement(normalizedChildren[next]) &&
                normalizedChildren[next].props?.disabled
              )
        )
      );
      actionRefs.current[next]?.focus();
    } else if (e.key === keyPrev) {
      e.preventDefault();
      let prev = idx;
      const total = normalizedChildren.length || (actions?.length || 0);
      if (total === 0) return;
      do {
        prev = (prev - 1 + total) % total;
      } while (
        prev !== idx &&
        (
          actions
            ? (actions[prev]?.disabled)
            : (
                React.isValidElement(normalizedChildren[prev]) &&
                normalizedChildren[prev].props?.disabled
              )
        )
      );
      actionRefs.current[prev]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      actionRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      const total = normalizedChildren.length || (actions?.length || 0);
      actionRefs.current[total - 1]?.focus();
    } else if (e.key === "Escape") {
      handleOpenChange(false);
    }
  };

  // Abre FAB via teclado
  const handleFabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (["Enter", " ", keyNext, keyPrev].includes(e.key)) {
      e.preventDefault();
      handleOpenChange(!open);
    }
    if (e.key === "Escape" && open) {
      e.preventDefault();
      handleOpenChange(false);
    }
  };

  function getButtonContent(props: BaseActionProps): string | null {
    // Se label existir e for string, retorna label
    if (typeof props.label === "string" && props.label) return props.label;
    // Se children for string, retorna children
    if (typeof props.children === "string") return props.children;
    // Se children for array de strings, retorna join
    if (Array.isArray(props.children) && props.children.every((c) => typeof c === "string")) {
      return props.children.join(" ");
    }
    // Caso contrário, retorna null
    return null;
  }

  // Função utilitária para decidir o placement do Tooltip conforme a direção do menu
  function getTooltipPlacement(direction: "up" | "down" | "left" | "right") {
    switch (direction) {
      case "left":
        return "right";
      case "right":
        return "left";
      case "down":
        return "top";
      case "up":
      default:
        return "left";
    }
  }

  // Tailwind direction classes
  const directionClasses = {
    up: "flex-col-reverse bottom-15 right-1",   // era right-2
    down: "flex-col top-20 right-1",            // era right-2
    left: "flex-row-reverse right-20 top-0",
    right: "flex-row left-20 top-0",
  };

  // Responsividade para telas pequenas
  const fabMenuPos = "absolute bottom-8 right-8 z-[1200] flex flex-col items-end sm:bottom-4 sm:right-4";
  const fabMainBtn = clsx(
    "w-14 h-14 min-w-[56px] min-h-[56px] rounded-full flex items-center justify-center transition-all duration-200",
    THEME[theme].fabBg,
    THEME[theme].fabColor,
    "shadow-md", // 6dp elevation
    THEME[theme].focusOutline,
    "text-[20px]", // Ícone 20dp
    "hover:scale-110 active:scale-95",
    "hover:shadow-lg",
    "outline-none",
    "!p-0"
  );
  const fabActionsBase = clsx(
    "absolute flex z-10 transition-all duration-200 items-end",
    directionClasses[direction],
    "gap-0.5", // 2dp entre botões (ainda mais próximo)
    open ? "pointer-events-auto opacity-100 translate-y-0 scale-100" : "pointer-events-none opacity-0 translate-y-4 scale-95"
  );
  const fabActionLi = "relative transition-transform duration-200";
  // Novo: classes para o item do menu (container do botão + label)
  const fabMenuItemContainer = clsx(
    "flex items-center h-14 min-h-[56px] px-1 gap-2", // era px-2
    "rounded-full", // shape full
    "shadow-none", // elevation level0
    "bg-transparent"
  );
  const fabMenuItemLabel = clsx(
    "ml-2", // espaço entre ícone e label: 8dp
    "font-medium",
    "text-sm leading-5 tracking-[0.15pt]", // fonte menor
    "font-inter"
  );
  const fabActionBtn = clsx(
    "w-12 h-12 min-w-[48px] min-h-[48px] rounded-full flex items-center justify-center transition-all duration-200",
    THEME[theme].actionBg,
    THEME[theme].actionColor,
    THEME[theme].actionHoverBg,
    THEME[theme].actionHoverColor,
    THEME[theme].focusOutline,
    "text-[24px]", // Ícone 24dp
    "shadow-none", // elevation level0
    "hover:scale-110 active:scale-95",
    "outline-none"
  );
  const fabTooltip = clsx(
    "absolute right-full mr-4 px-3 py-1 rounded shadow text-base whitespace-nowrap font-medium",
    THEME[theme].tooltipBg,
    THEME[theme].tooltipColor,
    "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 pointer-events-none"
  );

  // Backdrop classes
  const fabBackdrop = clsx(
    "fixed inset-0 z-[1199] transition-opacity duration-200",
    THEME[theme].backdrop,
    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
  );

  // Ajuste condição de exibição (permite 1 ação)
  const hasAnyAction = (actions && actions.length > 0) || normalizedChildren.length > 0;

  const menuContent = (
    <>
      {backdropVisible && (
        <div
          className={fabBackdrop}
          aria-hidden="true"
          onClick={() => handleOpenChange(false)}
        />
      )}
      <div className={clsx(fabMenuPos, className)}>
        <Button
          aria-label={label || "Abrir menu"}
          buttonProps={{
            'aria-haspopup': 'menu',
            'aria-expanded': open,
            'aria-controls': menuIdRef.current,
          }}
          onClick={() => handleOpenChange(!open)}
          ref={ref}
          size="large"
          theme={theme}
          buttonClassName={fabMainBtn}
          color="#1976d2"
          icon={
            <span
              className={clsx(
                "transition-transform duration-300",
                open ? "rotate-45" : "rotate-0"
              )}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
              }}
            >
              {icon || (
                <svg
                  width={20}
                  height={20}
                  viewBox="0 0 20 20"
                  stroke="currentColor"
                  fill="none"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: "block" }}
                  aria-hidden="true"
                >
                  <line x1="10" y1="4.5" x2="10" y2="15.5" />
                  <line x1="4.5" y1="10" x2="15.5" y2="10" />
                </svg>
              )}
            </span>
          }
          type="button"
          variant="fab"
          shape="round"
          onKeyDown={handleFabKeyDown} // <-- novo
        />
        {(hasAnyAction) && (
          <ul
            className={fabActionsBase}
            role="menu"
            id={menuIdRef.current}
            aria-label={label || "Menu de ações"}
          >
            {(actions ?? normalizedChildren).map((actionOrChild, idx) => {
              const action: z.infer<typeof FabMenuActionSchema> | BaseActionProps = actions
                ? (actionOrChild as z.infer<typeof FabMenuActionSchema>)
                : {
                    ...(actionOrChild as ReactElement<BaseActionProps>).props,
                    key: (actionOrChild as ReactElement<BaseActionProps>).key ?? idx,
                    icon: (actionOrChild as ReactElement<BaseActionProps>).props.icon,
                    label: getButtonContent((actionOrChild as ReactElement<BaseActionProps>).props),
                    onClick: (actionOrChild as ReactElement<BaseActionProps>).props.onClick,
                    tooltip: (actionOrChild as ReactElement<BaseActionProps>).props.tooltip,
                    color: (actionOrChild as ReactElement<BaseActionProps>).props.color,
                    disabled: (actionOrChild as ReactElement<BaseActionProps>).props.disabled,
                  };

              return (
                <li
                  key={typeof action.key === "string" || typeof action.key === "number" ? action.key : idx}
                  id={`fab-action-${menuIdRef.current}-${idx}`}
                  className={clsx(fabActionLi, "group")}
                  ref={(el) => { actionRefs.current[idx] = el; }}
                  style={{ transitionDelay: `${idx * 40}ms` }}
                  role="none"
                >
                  <div className={fabMenuItemContainer}>
                    <Button
                      key={action.key as React.Key}
                      className={fabActionBtn}
                      theme={theme}
                      size="medium"
                      variant="contained"
                      shape="round"
                      tooltip={undefined}
                      aria-label={action.label}
                      color={typeof action.color === "string" ? action.color : undefined}
                      disabled={typeof action.disabled === "boolean" ? action.disabled : undefined}
                      loading={typeof action.loading === "boolean" ? action.loading : undefined}
                      onClick={(e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
                        if (action.disabled) {
                          e?.preventDefault();
                          return;
                        }
                        if (action.onClick) {
                          if (e && "currentTarget" in e) {
                            action.onClick(e as React.MouseEvent<HTMLButtonElement>);
                          } else {
                            action.onClick(undefined as unknown as React.MouseEvent<HTMLButtonElement>);
                          }
                        }
                        if (closeOnAction) handleOpenChange(false); // <-- respeita nova prop
                      }}
                      onKeyDown={(e) =>
                        open &&
                        handleActionKeyDown(
                          e,
                          idx,
                          typeof action.disabled === "boolean" ? action.disabled : undefined
                        )
                      }
                      tabIndex={open ? 0 : -1}
                    >
                      {typeof action.label === "string" && action.label ? (
                        <span className={fabMenuItemLabel}>{action.label}</span>
                      ) : null}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      {/* Renderize Tooltips fora do <ul> para evitar filhos inválidos */}
      {(actions ?? normalizedChildren).map(
        (
          actionOrChild: z.infer<typeof FabMenuActionSchema> | ReactElement<BaseActionProps>,
          idx: number
        ) => {
          const action: z.infer<typeof FabMenuActionSchema> | BaseActionProps = actions
            ? (actionOrChild as z.infer<typeof FabMenuActionSchema>)
            : {
                ...(actionOrChild as ReactElement<BaseActionProps>).props,
                key: (actionOrChild as ReactElement<BaseActionProps>).key ?? idx,
                icon: (actionOrChild as ReactElement<BaseActionProps>).props.icon,
                label: getButtonContent((actionOrChild as ReactElement<BaseActionProps>).props),
                onClick: (actionOrChild as ReactElement<BaseActionProps>).props.onClick,
                tooltip: (actionOrChild as ReactElement<BaseActionProps>).props.tooltip,
                color: (actionOrChild as ReactElement<BaseActionProps>).props.color,
                disabled: (actionOrChild as ReactElement<BaseActionProps>).props.disabled,
              };
          // Só renderiza tooltip se existir
          if (!(action.tooltip || typeof action.label === "string")) return null;
          return (
            <Tooltip
              key={`tooltip-${typeof action.key === "string" || typeof action.key === "number" ? action.key : idx}`}
              tooltip={String(action.tooltip || (typeof action.label === "string" ? action.label : ""))}
              placement={getTooltipPlacement(direction)}
              openDelay={0}
              closeDelay={0}
              className={fabTooltip}
              // Associe o tooltip ao botão pelo id
              anchorId={`fab-action-${menuIdRef.current}-${idx}`}
            />
          );
        }
      )}
      </div>
    </>
  );

  return portal
    ? ReactDOM.createPortal(menuContent, document.body)
    : menuContent;
});

FabMenuComponent.displayName = "FabMenu";
export default FabMenuComponent;

/*
  Observação:
  - Certifique-se de que as classes Tailwind como z-[1200], bg-[#1976d2], etc., estão no safelist do seu tailwind.config.js.
  - Se quiser animações mais avançadas, pode adicionar plugins ou CSS custom.
*/